import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Query, useMutation } from 'react-apollo';
import { Formik, Form, Field, FieldArray } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from 'components/common/Button';
import TextField from 'components/form/Formik/FormikInput';
import Select from 'components/form/Formik/FormikReactSelect';
import DateInput from 'components/form/Formik/FormikDateInput';
import Rules from './components/Rules';
import { ADD_CAMPAIGN, UPDATE_CAMPAIGN } from 'graphql/campaigns';
import useSnack from 'hooks/useSnack';
import Variables from './components/Variables';
import Recipients from "./components/Recipients";

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 800,
    padding: theme.spacing(2),
  },
}));

const required = (value) => (!value ? 'This field is required' : null);


const CreateCampaign = ({ history, refetch, campaign }) => {
  const classes = useStyles();
  const [createCampaign] = useMutation(ADD_CAMPAIGN);
  const [updateCampaign] = useMutation(UPDATE_CAMPAIGN);
  const snack = useSnack();
  const user = useSelector(s => s.user.user);
  const [excludedUsers, setExcludedUsers] = useState(campaign ? campaign.recipients.exclude_users : [])

  const onClose = () => history.goBack();

  const onSubmit = async (values) => {
    try {
      const { recipients, ...other } = values;
      const payload = other;
      if (recipients.type === 'all') {
        payload.recipients = {
          type: 'all',
          include_past_students: recipients.include_past_students || false,
        }
      } else if (recipients.type === 'users') {
        payload.recipients = {
          type: 'users',
          users: recipients.users
        }
      } else {
        payload.recipients = {
          type: 'filters',
          filters: recipients.filters,
          exclude_users: excludedUsers,
        }
      }

      if (campaign?.id) {
        await updateCampaign({
          variables: {
            id: campaign.id,
            data:{
              ...payload,
              created_by: user.id,
            }
          }
        })
      } else {
        await createCampaign({
          variables: {
            data: {
              ...payload,
              created_by: user.id,
            }
          }
        });
      }
      refetch();
      onClose();
    } catch (e) {
      snack.error(e.message);
    }
  };

  return (
    <Drawer
      open
      anchor="right"
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box mb={3}>
        <Typography variant="h4">
          {campaign?.id ? 'Edit Campaign' : 'Create Campaign'}
        </Typography>
      </Box>
      <Formik initialValues={{
        name: campaign?.name || '',
        delivery_type: campaign?.delivery_type || '',
        delivery_at: campaign?.delivery_at || '',
        message: campaign?.message || '',
        recipients: campaign?.recipients || {}
      }} onSubmit={onSubmit}>
        {({ isSubmitting, handleSubmit, values }) => {
          return (
            <Form>
              <Grid container spacing={2}>
                <Grid item md={8}>
                  <Field
                    fullWidth
                    name="name"
                    label="Campaign name"
                    component={TextField}
                    validate={required}
                  />
                </Grid>
                <Grid item md={4}>
                  <Field
                    fullWidth
                    name="delivery_type"
                    transformValue
                    isMulti={false}
                    options={[
                      { label: 'SMS', value: 'sms' },
                      { label: 'Chat', value: 'chat' },
                      { label: 'App notification', value: 'app' },
                    ]}
                    label="Delivery via"
                    component={Select}
                    validate={required}
                  />
                </Grid>
              </Grid>
              <Field
                time
                fullWidth
                inputVariant="outlined"
                label="Delivery at"
                name="delivery_at"
                component={DateInput}
                validate={required}
              />
              <Rules recipients={values.recipients} />
              {values.recipients.type === 'filters' && (
                  <Recipients
                      delivery_type={values.delivery_type}
                      recipients={values.recipients}
                      excluded={excludedUsers}
                      setExcluded={setExcludedUsers}
                  />
              )}
              <Field
                component={TextField}
                validate={required}
                name="message"
                label="Message"
                multiline
                rows={6}
              />
              <Variables />
              <Box textAlign="right" mt={2}>
                <Button onClick={onClose} style={{ marginRight: 8 }}>
                  Cancel
                </Button>
                <Button
                  disabled={isSubmitting || (campaign && campaign.ran_at)}
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export default CreateCampaign;
